import { useTranslation } from 'react-i18next';
import { Card } from '../common';
import { useCalculationStore } from '../../stores/calculationStore';
import { useProductionStore } from '../../stores/productionStore';
import { usePricingStore } from '../../stores/pricingStore';
import { useExpenseStore } from '../../stores/expenseStore';
import { useCurrency } from '../../lib/utils/currency';
import { useUnitStore, KG_TO_LB } from '../../stores/unitStore';

export function RevenueBreakdown() {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { weightUnit } = useUnitStore();
  const revenue = useCalculationStore((state) => state.revenue);
  const taxes = useCalculationStore((state) => state.taxes);
  const metrics = useCalculationStore((state) => state.metrics);

  if (!revenue || !taxes || !metrics) {
    return (
      <Card title={t('results.revenueBreakdown')}>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
          {t('results.enterRequiredData')}
        </p>
      </Card>
    );
  }

  // Calculate average weight from production data
  const productionStore = useProductionStore.getState();
  const harvestEvents = productionStore.harvestEvents;
  const totalCount = harvestEvents.reduce((sum, event) => sum + event.count, 0);
  const totalWeight = harvestEvents.reduce((sum, event) => sum + (event.totalWeight || 0), 0);
  const averageWeight = totalCount > 0 ? totalWeight / totalCount : 0;

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getValueColor = (value: number): string => {
    if (value > 0) return 'text-green-600 dark:text-green-400';
    if (value < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-900 dark:text-gray-100';
  };

  // Get pricing parameters
  const pricingParams = usePricingStore.getState().parameters;
  const productionData = productionStore.data;
  
  // Calculate detailed breakdown values
  const fcrDifference = metrics.targetFCR - metrics.fcr;
  const savedKg = fcrDifference * totalWeight;
  const feedRate = savedKg >= 0 ? pricingParams.feedBonusRate : pricingParams.feedPenaltyRate;
  
  const allowedDeaths = productionData.chickCount * (pricingParams.chickMortalityLimit / 100);
  const actualDeaths = productionData.chickCount - totalCount;
  const chickDifference = allowedDeaths - actualDeaths;
  const chickRate = chickDifference >= 0 ? pricingParams.chickBonusRate : pricingParams.chickPenaltyRate;
  
  // Find coal price from expenses
  const expenses = useExpenseStore.getState().expenses;
  const coalExpenses = expenses.filter(e => e.category === 'coal');
  
  // Calculate total coal tonnage from all coal expenses
  const totalCoalTonnage = coalExpenses.reduce((sum, expense) => sum + (expense.tonnage || 0), 0);
  
  // Get coal unit price from first expense (for support calculation)
  const coalExpense = coalExpenses[0];
  const coalPricePerTonWithVAT = coalExpense ? coalExpense.unitPrice : 0;
  const coalPricePerTonExclVAT = (coalPricePerTonWithVAT || 0) / 1.20; // Remove 20% VAT
  const supportedCoalTons = coalPricePerTonExclVAT > 0 ? revenue.coalSupport / coalPricePerTonExclVAT : 0;
  
  // Calculate support percentage
  const supportPercentage = totalCoalTonnage > 0 ? (supportedCoalTons / totalCoalTonnage) * 100 : 0;
  
  // Determine EPEF premium rate
  let epefPremiumRate = 0;
  if (metrics.epef > pricingParams.epefLimit3) {
    epefPremiumRate = pricingParams.epefPrice3;
  } else if (metrics.epef > pricingParams.epefLimit2) {
    epefPremiumRate = pricingParams.epefPrice2;
  } else if (metrics.epef > pricingParams.epefLimit1) {
    epefPremiumRate = pricingParams.epefPrice1;
  }

  const displayWeight = weightUnit === 'lb' ? totalWeight * KG_TO_LB : totalWeight;
  const displaySavedKg = weightUnit === 'lb' ? Math.abs(savedKg) * KG_TO_LB : Math.abs(savedKg);
  
  const revenueItems: Array<{
    label: string;
    value: number;
    icon: string;
    detail: string;
    extraDetail?: string;
  }> = [
    { 
      label: t('results.meatRevenue'), 
      value: revenue.meatRevenue, 
      icon: '🥩',
      detail: `${formatCurrency(displayWeight)} ${weightUnit} × ${formatCurrency(pricingParams.meatPricePerKg)} ${symbol}/${weightUnit}`
    },
    { 
      label: t('results.feedBonus'), 
      value: revenue.feedBonus, 
      icon: '🌾',
      detail: `${formatCurrency(displaySavedKg)} ${weightUnit} × ${formatCurrency(feedRate)} ${symbol}/${weightUnit}`
    },
    { 
      label: t('results.chickBonus'), 
      value: revenue.chickBonus, 
      icon: '🐣',
      detail: `${Math.abs(chickDifference).toFixed(0)} ${t('results.count')} × ${formatCurrency(chickRate)} ${symbol}/${t('results.count')}`
    },
    { 
      label: t('results.epefPremium'), 
      value: revenue.epefPremium, 
      icon: '⭐',
      detail: revenue.epefPremium > 0 ? `${formatCurrency(displayWeight)} ${weightUnit} × ${formatCurrency(epefPremiumRate)} ${symbol}/${weightUnit}` : t('results.noPremium')
    },
    { 
      label: t('results.coalSupport'), 
      value: revenue.coalSupport, 
      icon: '🔥',
      detail: `${formatCurrency(displayWeight)} ${weightUnit} × ${formatCurrency(pricingParams.coalSupportPerTon)} ${symbol}/${weightUnit}`,
      extraDetail: coalPricePerTonExclVAT > 0 && totalCoalTonnage > 0
        ? `${t('results.coalUsed')}: ${totalCoalTonnage.toFixed(2)} ton\n${t('results.coalSupported')}: ${supportedCoalTons.toFixed(2)} ton (%${supportPercentage.toFixed(1)})`
        : coalPricePerTonExclVAT > 0
        ? `${t('results.coalSupported')}: ${supportedCoalTons.toFixed(2)} ton`
        : undefined
    },
  ];

  return (
    <Card title={t('results.revenueBreakdown')}>
      <div className="space-y-3">
        {/* Revenue Items */}
        {revenueItems.map((item, index) => (
          <div
            key={index}
            className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <span className="text-xl">{item.icon}</span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {item.label}
                </span>
              </div>
              <span className={`text-sm font-semibold ${getValueColor(item.value)}`}>
                {item.value >= 0 ? '+' : ''}{formatCurrency(item.value)} {symbol}
              </span>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 ml-8">
              {item.detail}
              {item.extraDetail && (
                <div className="mt-1 text-blue-600 dark:text-blue-400 font-medium whitespace-pre-line">
                  {item.extraDetail}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Gross Revenue Total */}
        <div className="pt-3 border-t-2 border-gray-300 dark:border-gray-600">
          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">
              {t('results.grossRevenue')}
            </span>
            <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
              {formatCurrency(revenue.grossRevenue)} {symbol}
            </span>
          </div>
        </div>

        {/* Per Bird Revenue - Without VAT */}
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('results.perBirdRevenue')}
            </span>
            <span className="text-base font-semibold text-green-600 dark:text-green-400 whitespace-nowrap">
              {formatCurrency(averageWeight * taxes.pricePerKg)} {symbol}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {(weightUnit === 'lb' ? (averageWeight || 0) * KG_TO_LB : (averageWeight || 0)).toFixed(3)} {weightUnit} × {formatCurrency(taxes.pricePerKg)} {symbol}/{weightUnit}
          </div>
        </div>

        {/* Info Box */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-start space-x-2">
            <span>ℹ️</span>
            <div>
              <p className="font-medium mb-1">{t('results.revenueComponents')}</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>{t('results.greenValuesBonus')}</li>
                <li>{t('results.redValuesPenalty')}</li>
                <li>{t('results.grossRevenueSum')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
